import './styles.scss';

import { Card, Collapse } from 'react-bootstrap';
import { TODAY, TODAY_EOD } from '@/constants';
import { useEffect, useRef, useState } from 'react';

import Chart from '@/common/Chart';
import { Datetime } from '@/utils/dates';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { getDtrStatistic } from '@/features/DriveTimeTool/api/services/getDtrStatistic';
import { useDriveTimeTool } from '@/features/DriveTimeTool';

const BOUNDS_COLOR = '#D7DEE4';
const NINETY_NINE_P_COLOR = '#EA3323';
const NINETY_FIVE_P_COLOR = '#ED6D68';
const NINETY_P_COLOR = '#F09150';
const SIXTY_P_COLOR = '#F7CE81';
const EIGHTY_P_COLOR = '#F3AF7D';
const AVG_DT_COLOR = '#8F90F8';
const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';

type Plots = [x: string, y: number][];
type Stats = {
  avgDt: Plots;
  stdDeviationLowerBound: Plots;
  stdDeviationUpperBound: Plots;
  p60: Plots;
  p80: Plots;
  p90: Plots;
  p95: Plots;
  p99: Plots;
  startDatetime: string;
  endDatetime: string;
  maxY: number;
  id: string;
};
const initDriveTimeDeviationsStats: Stats = {
  avgDt: [],
  stdDeviationLowerBound: [],
  stdDeviationUpperBound: [],
  p60: [],
  p80: [],
  p90: [],
  p95: [],
  p99: [],
  startDatetime: TODAY,
  endDatetime: TODAY_EOD,
  maxY: null,
  id: null,
};

export type DriveTimeDeviationsProps = { height?: string; title?: string; disableMiniMap?: boolean };

const DriveTimeDeviations = ({ height, title, disableMiniMap }: DriveTimeDeviationsProps): JSX.Element => {
  const [{ selected, selectedFrom, loading }, setState] = useDriveTimeTool(({ state, setState }) => [
    { selected: state.selected, selectedFrom: state.selectedFrom || 'queryHistoryId', loading: state.loading.deviations },
    setState,
  ]);
  const [stats, setStats] = useState(initDriveTimeDeviationsStats);
  const [isExpanded, setIsExpanded] = useState(false);

  const chartsRef = useRef<ReactEChartsCore>(null);

  const onToggleExpand = () => setIsExpanded((current) => !current);

  const fetchData = async (id: string): Promise<void> => {
    try {
      if (loading) return;
      setState((state) => ({ ...state, loading: { ...state.loading, deviations: true } }));
      const res = await getDtrStatistic({ [selectedFrom]: selected });
      const sorted = res || [];
      // .sort((a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime());
      const state: Stats = {
        avgDt: [],
        stdDeviationLowerBound: [],
        stdDeviationUpperBound: [],
        p60: [],
        p80: [],
        p90: [],
        p95: [],
        p99: [],
        startDatetime: TODAY,
        endDatetime: TODAY_EOD,
        maxY: null,
        id,
      };

      for (let i = 0; i < sorted.length; i++) {
        const node = sorted[i];

        const timeStamp = new Datetime(node.timeStamp);
        if (i === 0) state.startDatetime = timeStamp.clone().startOf('day').toString();
        if (i === sorted.length - 1) state.endDatetime = timeStamp.clone().add(1, 'day').startOf('day').toString();

        const push = (arr, y) => {
          arr.push([new Date(node.timeStamp).getTime(), y]);
          if (y > state.maxY) state.maxY = y;
        };
        push(state.avgDt, node.avgDt);
        push(state.stdDeviationLowerBound, node.stdDeviationLowerBound);
        push(state.stdDeviationUpperBound, node.stdDeviationUpperBound);
        push(state.p60, node.p60);
        push(state.p80, node.p80);
        push(state.p90, node.p90);
        push(state.p95, node.p95);
        push(state.p99, node.p99);
      }

      setStats(state);
    } catch (e) {
      console.error('fetchData', e);
    } finally {
      setState((state) => ({ ...state, loading: { ...state.loading, deviations: false } }));
    }
  };

  useEffect(() => {
    if (!selected) return setStats(initDriveTimeDeviationsStats);
    fetchData(selected);
  }, [selected]);

  useEffect(() => {
    if (!stats.id || !isExpanded) return;
    const chart = chartsRef.current?.getEchartsInstance();
    chart.resize();
  }, [isExpanded, stats.id]);

  return (
    <div className="DriveTimeDeviations">
      <Card className="deviations-card">
        <Card.Body>
          <div className="deviations-header" onClick={onToggleExpand}>
            <div className="header-content">
              <div className="header-left">
                <div className="chart-icon">
                  <i className="sv sv-chart-growth"></i>
                </div>
                <div className="header-text">
                  <h5 className="header-title">Drive Time Deviations Chart</h5>
                  <p className="header-subtitle">Analyze drive time variations and deviations from average</p>
                </div>
              </div>
              <div className="collapse-icon">
                <i
                  className={`fas fs-4 ${loading ? 'fa fa-spinner fa-pulse opacity-50' : isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                />
              </div>
            </div>
          </div>

          <Collapse in={isExpanded}>
            <div className="deviations-content">
              <div className="chart-section">
                {!stats.avgDt.length && (
                  <div className="chart-placeholder">
                    <div className="chart-icon-large">
                      <i className="sv sv-chart-growth"></i>
                    </div>
                    <h4 className="placeholder-title">Select queries to view drive time percentile analysis.</h4>
                    <p className="placeholder-description">Shows average drive times with percentile distribution curves.</p>
                  </div>
                )}
                {stats.avgDt.length > 0 && (
                  <>
                    <Chart
                      style={{ height: height || '57rem', width: '100%' }}
                      notMerge={true}
                      lazyUpdate={true}
                      ref={chartsRef}
                      option={{
                        ...GetEchartsOptionsForDriveTimeDeviation(stats, disableMiniMap !== undefined && disableMiniMap !== false),
                        title: {
                          text: title,
                          left: 'center', // This centers the title horizontally
                          textStyle: {
                            fontWeight: 700,
                            fontSize: 12,
                          },
                        },
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          </Collapse>
        </Card.Body>
      </Card>
    </div>
  );
};

const GetEchartsOptionsForDriveTimeDeviation = (stats: Stats, disableMiniMap: boolean) => ({
  grid: {
    top: 20, // Adjust this value to control the distance from the top of the chart container
    left: '3%',
    right: 120,
    bottom: 60,
    containLabel: true, // Ensures labels are contained within the grid
  },
  dataZoom: !disableMiniMap
    ? [
        {
          type: 'inside',
          filterMode: 'none', // important for time axis
          zoomOnMouseWheel: 'shift', // scroll wheel zoom
          moveOnMouseMove: true, // drag-to-pan
          xAxisIndex: [0],
          minSpan: 1, // cannot zoom in beyond 1 hour
          maxSpan: null, // but can zoom out infinitely
        },
        {
          type: 'slider',
          xAxisIndex: [0],
          realtime: true,
          filterMode: 'none',
          height: 20,
          bottom: 20,
          minSpan: 1, // cannot zoom in beyond 1 hour
          maxSpan: null, // but can zoom out infinitely
        },
        {
          type: 'inside',
          yAxisIndex: [0],
          filterMode: 'none',
          minSpan: 10,
          maxSpan: null, // but can zoom out infinitely
          zoomOnMouseWheel: 'shift',
        },
      ]
    : undefined,
  useUTC: true, // Set this to true
  xAxis: {
    type: 'time',
    name: 'Per 60 minutes',
    nameLocation: 'middle',
    nameGap: 20,
    nameTextStyle: {
      fontWeight: 700,
    },
    min: (value) => value.min,
    max: (value) => value.max,
    interval: 2,
    splitLine: {
      show: false,
    },
  },
  yAxis: {
    type: 'value',
    name: 'Duration (ms)',
    nameLocation: 'middle',
    nameGap: 30,
    nameTextStyle: {
      fontWeight: 700,
    },
    min: 0,
    max: stats.maxY || 120,
    interval: Math.ceil((stats.maxY || 120) / 12),
    splitLine: {
      show: false,
    },
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
    },
    backgroundColor: '#333', // dark grey
    textStyle: {
      color: '#fff',
    },
    formatter: (params) => {
      // params = array of all series at that x position
      const time = new Datetime(params[0].axisValue); // timestamp string
      const title = `<strong>${time.format(DATETIME_FORMAT)}</strong>`;
      let body = '';
      params.forEach((param) => {
        let value = '';
        if (param.seriesName === 'Raw Points') return;
        // format value
        switch (param.seriesName) {
          case 'P60 (60)':
          case 'P80 (80)':
          case 'P90 (90)':
          case 'P95 (95)':
          case 'P99 (99)':
            value = `${param.value[1]}mins`; // param.value.toFixed(2);
            break;
          default:
            value = `${param.value[1].toFixed(2)}`;
            break;
        }

        const marker = `<div style="display:block;width:10px;height:100%;background-color:${param.color};"></div>`;

        body += `<div class="d-flex justify-content-between flex-nowrap w-100 gap-3" style="min-height:2.5rem;">
                  <div class="d-flex align-items-center gap-2 flex-nowrap">${marker}${param.seriesName}</div>
                  <div class="d-flex align-items-center">
                    <strong>${value}</strong>
                  </div>
                </div>
                `;
      });

      return `
              <div class="pb-2 mb-2 border-bottom border-1 border-gray-subtle">${title}</div>
              <div>${body}</div>
              `;
    },
  },
  // Legend positioned on the right as requested
  legend: {
    data: ['Upper', 'Lower', 'P60 (60)', 'P80 (80)', 'P90 (90)', 'P95 (95)', 'P99 (99)', 'AvgDT'],
    orient: 'vertical',
    right: 10,
    top: 'auto',
  },
  series: [
    // Standard Deviation Band (Area)
    {
      name: 'Lower',
      type: 'line',
      data: stats.stdDeviationLowerBound,
      smooth: true,
      showSymbol: false,
      lineStyle: { opacity: 0 },
      itemStyle: { color: BOUNDS_COLOR },
      areaStyle: {
        color: BOUNDS_COLOR,
        opacity: 0.25, // << added, this matters
      },
      z: 1,
    },
    {
      name: 'Upper',
      type: 'line',
      data: stats.stdDeviationUpperBound,
      smooth: true,
      showSymbol: false,
      lineStyle: { opacity: 0 },
      itemStyle: { color: BOUNDS_COLOR },
      areaStyle: {
        color: BOUNDS_COLOR,
        opacity: 0.5,
        origin: 'start', // fills BETWEEN lower and upper
      },
      z: 2,
    },
    // Percentile Lines
    {
      name: 'P60 (60)',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: stats.p60,
      itemStyle: { color: SIXTY_P_COLOR },
    },
    {
      name: 'P80 (80)',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: stats.p80,
      itemStyle: { color: EIGHTY_P_COLOR },
    },
    {
      name: 'P90 (90)',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: stats.p90,
      itemStyle: { color: NINETY_P_COLOR },
    },
    {
      name: 'P95 (95)',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: stats.p95,
      itemStyle: { color: NINETY_FIVE_P_COLOR },
    },
    {
      name: 'P99 (99)',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: stats.p99,
      itemStyle: { color: NINETY_NINE_P_COLOR },
    },
    // Average Line (AvgDT)
    {
      name: 'AvgDT',
      type: 'line',
      smooth: true,
      showSymbol: false,
      data: stats.avgDt,
      itemStyle: { color: AVG_DT_COLOR },
    },
  ],
});

export default DriveTimeDeviations;
